import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { usePortfolioReportsQuery } from '../../api/usePortfolioReportsQuery';
import { setSelectedReport } from '../../state';
import { AsyncComponentHookResult } from '../../../Types/ComponentHookResult';
import { ReportOption } from '../../types';
import { GroupType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';

const UNCATEGORIZED_REPORTS_GROUP_LABEL = 'Uncategorized';

interface UseReportSelectData {
  reportOptions: GroupType<ReportOption>[];
}

interface UseReportSelectActions {
  onSelectReport(report: ReportOption): void;
}

export const useReportSelect = (): AsyncComponentHookResult<
  UseReportSelectData,
  UseReportSelectActions
> => {
  const queryInfo = usePortfolioReportsQuery();

  const dispatch = useDispatch();

  const onSelectReport = report => {
    dispatch(setSelectedReport(report));
  };

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const reportOptions = queryInfo.data.reduce(
      (acc, report) => {
        const { category } = report;

        const reportOption: ReportOption = {
          ...report,
          label: report.name,
          value: report.id,
        };

        if (!category) {
          acc[acc.length - 1].options.push(reportOption);
        } else {
          const foundCategory = acc.find(({ label }) => label === category);

          if (foundCategory) {
            foundCategory.options.push(reportOption);
          } else {
            acc.unshift({ label: category, options: [reportOption] });
          }
        }

        return acc;
      },
      [{ label: UNCATEGORIZED_REPORTS_GROUP_LABEL, options: [] as ReportOption[] }],
    );

    const sortedReportOptions = reportOptions.map(reportOption => ({
      ...reportOption,
      options: reportOption.options.sort((a, b) => {
        if (a.name > b.name) {
          return 1;
        }
        if (a.name < b.name) {
          return -1;
        }
        return 0;
      }),
    }));

    return {
      reportOptions: sortedReportOptions,
    };
  }, [queryInfo.data]);

  return {
    ...queryInfo,
    data,
    actions: {
      onSelectReport,
    },
  };
};
